import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  ArrowLeft, Download, Trash2, UserX, UserPlus, ShieldCheck,
  AlertTriangle, CheckCircle, Loader2,
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg';

export default function PrivacySettingsPage() {
  const { token, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const [deletionStatus, setDeletionStatus] = useState(null);
  const [nominee, setNominee] = useState(null);
  const [grievance, setGrievance] = useState(null);

  // dialogs
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [nominateOpen, setNominateOpen] = useState(false);

  // form state for delete
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [withdrawConfirmed, setWithdrawConfirmed] = useState(false);

  // nominee form
  const [nomineeForm, setNomineeForm] = useState({
    nominee_name: '', nominee_relationship: '', nominee_email: '', nominee_phone: '', note: '',
  });

  const [banner, setBanner] = useState(null); // {type: 'success'|'error', text}

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    refresh();
    fetch(`${API}/dpdp/grievance-officer`).then((r) => r.json()).then(setGrievance).catch(() => {});
  }, [isAuthenticated]);

  const refresh = async () => {
    try {
      const [st, nm] = await Promise.all([
        axios.get(`${API}/dpdp/deletion-status`, { headers: authHeaders, withCredentials: true }),
        axios.get(`${API}/dpdp/nominate`, { headers: authHeaders, withCredentials: true }),
      ]);
      setDeletionStatus(st.data);
      setNominee(nm.data.nominee || null);
      if (nm.data.nominee) {
        setNomineeForm({
          nominee_name: nm.data.nominee.nominee_name || '',
          nominee_relationship: nm.data.nominee.nominee_relationship || '',
          nominee_email: nm.data.nominee.nominee_email || '',
          nominee_phone: nm.data.nominee.nominee_phone || '',
          note: nm.data.nominee.note || '',
        });
      }
    } catch (e) {
      // Errors from either endpoint are surfaced via per-action banners on
      // subsequent user actions. Log here for developer diagnostics.
      console.warn('[privacy] refresh(deletion-status/nominee) failed:', e?.response?.status, e?.message);
    }
  };

  const handleExport = async () => {
    setBanner(null);
    setSubmitting(true);
    try {
      const r = await axios.get(`${API}/dpdp/export-my-data`, { headers: authHeaders, withCredentials: true });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `misyou-data-export-${stamp}.json`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      setBanner({ type: 'success', text: 'Your data has been downloaded as misyou-data-export.json.' });
    } catch (e) {
      setBanner({ type: 'error', text: e?.response?.data?.detail || 'Failed to export data.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestDelete = async () => {
    setBanner(null);
    if (!deleteConfirmed) {
      setBanner({ type: 'error', text: 'Please tick the confirmation box first.' });
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/dpdp/delete-my-account`, {
        reason: deleteReason, confirm: true,
      }, { headers: authHeaders, withCredentials: true });
      setDeletionStatus({
        is_pending_deletion: true,
        deletion_effective_at: r.data.deletion_effective_at,
        grace_days_remaining: r.data.grace_days_remaining,
      });
      setDeleteOpen(false);
      setDeleteReason(''); setDeleteConfirmed(false);
      setBanner({ type: 'success', text: r.data.message });
    } catch (e) {
      setBanner({ type: 'error', text: e?.response?.data?.detail || 'Failed to schedule deletion.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelDelete = async () => {
    setBanner(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/dpdp/cancel-deletion`, {}, { headers: authHeaders, withCredentials: true });
      setDeletionStatus({ is_pending_deletion: false });
      setBanner({ type: 'success', text: 'Deletion request cancelled.' });
    } catch (e) {
      setBanner({ type: 'error', text: e?.response?.data?.detail || 'Failed to cancel.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawConsent = async () => {
    setBanner(null);
    if (!withdrawConfirmed) {
      setBanner({ type: 'error', text: 'Please tick the confirmation box first.' });
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/dpdp/withdraw-consent`, { confirm: true }, { headers: authHeaders, withCredentials: true });
      setWithdrawOpen(false);
      setWithdrawConfirmed(false);
      setBanner({ type: 'success', text: r.data.message });
    } catch (e) {
      setBanner({ type: 'error', text: e?.response?.data?.detail || 'Failed to withdraw consent.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveNominee = async () => {
    setBanner(null);
    if (!nomineeForm.nominee_name || !nomineeForm.nominee_relationship) {
      setBanner({ type: 'error', text: 'Nominee name and relationship are required.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/dpdp/nominate`, nomineeForm, { headers: authHeaders, withCredentials: true });
      setNominateOpen(false);
      await refresh();
      setBanner({ type: 'success', text: 'Nominee saved.' });
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join('; ') : (d || 'Failed to save nominee.');
      setBanner({ type: 'error', text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveNominee = async () => {
    setBanner(null);
    setSubmitting(true);
    try {
      await axios.delete(`${API}/dpdp/nominate`, { headers: authHeaders, withCredentials: true });
      setNominee(null);
      setNomineeForm({ nominee_name: '', nominee_relationship: '', nominee_email: '', nominee_phone: '', note: '' });
      setBanner({ type: 'success', text: 'Nominee removed.' });
    } catch (e) {
      setBanner({ type: 'error', text: e?.response?.data?.detail || 'Failed to remove nominee.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-bold text-slate-900">MISYOU</span>
          </Link>
          <Link to="/dashboard">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-24" data-testid="privacy-settings-page">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-indigo-700 mb-2">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-sm font-semibold uppercase tracking-wider">DPDP Act, 2023</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Privacy &amp; your data</h1>
          <p className="text-muted-foreground mt-2">
            Exercise your rights as a Data Principal under the Digital Personal Data Protection Act, 2023.
          </p>
        </div>

        {banner && (
          <Alert className={`mb-6 ${banner.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            {banner.type === 'success'
              ? <CheckCircle className="h-4 w-4 text-emerald-600" />
              : <AlertTriangle className="h-4 w-4 text-red-600" />}
            <AlertDescription className={banner.type === 'success' ? 'text-emerald-800' : 'text-red-800'}>
              {banner.text}
            </AlertDescription>
          </Alert>
        )}

        {/* Pending deletion banner */}
        {deletionStatus?.is_pending_deletion && (
          <Alert className="mb-6 bg-amber-50 border-amber-300" data-testid="pending-deletion-banner">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-amber-900">
              Your account is scheduled for permanent deletion in
              {' '}<strong>{deletionStatus.grace_days_remaining}</strong> day(s)
              {' '}(on {new Date(deletionStatus.deletion_effective_at).toLocaleDateString()}).
              <Button
                size="sm" variant="outline"
                className="ml-3 border-amber-400 text-amber-900 hover:bg-amber-100"
                onClick={handleCancelDelete} disabled={submitting}
                data-testid="cancel-deletion-btn"
              >
                Cancel deletion
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Export */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-indigo-600" />
                Download my data
              </CardTitle>
              <CardDescription>
                Get a complete JSON copy of everything we hold about you — profile, cases,
                sightings, payments, notifications and your consent history.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleExport} disabled={submitting} data-testid="export-data-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download my data (JSON)
              </Button>
              <p className="text-xs text-muted-foreground mt-2">DPDP §11(1)(a) — right of access.</p>
            </CardContent>
          </Card>

          {/* Withdraw consent */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserX className="w-5 h-5 text-amber-600" />
                Withdraw consent
              </CardTitle>
              <CardDescription>
                Stop further processing of your data. You can still sign in to view existing
                records, but you won't be able to post new cases or sightings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="border-amber-400 text-amber-700 hover:bg-amber-50"
                onClick={() => setWithdrawOpen(true)}
                data-testid="open-withdraw-consent-btn"
              >
                Withdraw consent
              </Button>
              <p className="text-xs text-muted-foreground mt-2">DPDP §6(4) — as easy as giving it.</p>
            </CardContent>
          </Card>

          {/* Delete account */}
          <Card className="shadow-sm border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="w-5 h-5" />
                Delete my account
              </CardTitle>
              <CardDescription>
                We will schedule your account for deletion. You have a
                {' '}<strong>30-day grace window</strong> to change your mind by signing in
                and clicking "Cancel deletion".
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="border-red-400 text-red-700 hover:bg-red-50"
                onClick={() => setDeleteOpen(true)}
                disabled={deletionStatus?.is_pending_deletion}
                data-testid="open-delete-account-btn"
              >
                {deletionStatus?.is_pending_deletion ? 'Deletion already scheduled' : 'Request account deletion'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">DPDP §12(3) — right to erasure.</p>
            </CardContent>
          </Card>

          {/* Nominate */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-emerald-600" />
                Nominate a representative
              </CardTitle>
              <CardDescription>
                Nominate another individual (e.g., spouse, sibling) to exercise these
                rights on your behalf in case of your death or incapacity.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {nominee ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm mb-3" data-testid="current-nominee">
                  <p><strong>{nominee.nominee_name}</strong> ({nominee.nominee_relationship})</p>
                  {nominee.nominee_email && <p className="text-xs">{nominee.nominee_email}</p>}
                  {nominee.nominee_phone && <p className="text-xs">{nominee.nominee_phone}</p>}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mb-3" data-testid="no-nominee">No nominee on file.</p>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => setNominateOpen(true)} data-testid="open-nominate-btn">
                  {nominee ? 'Update nominee' : 'Add nominee'}
                </Button>
                {nominee && (
                  <Button variant="outline" onClick={handleRemoveNominee} disabled={submitting} data-testid="remove-nominee-btn">
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">DPDP §13 — right to nominate.</p>
            </CardContent>
          </Card>
        </div>

        {/* Grievance Officer card */}
        <Card className="mt-6 shadow-sm border-indigo-200 bg-indigo-50/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-indigo-900">
              <ShieldCheck className="w-5 h-5" />
              Grievance Officer
            </CardTitle>
            <CardDescription>
              Contact our DPDP-appointed Grievance Officer if these in-app tools don't
              resolve your issue.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm space-y-1 text-indigo-900" data-testid="grievance-officer-card">
            <p><strong>Name:</strong> {grievance?.name || 'Mr. Naresh Kumar'}</p>
            <p>
              <strong>Email:</strong>{' '}
              <a className="underline" href={`mailto:${grievance?.email || 'grievance@misyou.in'}`}>
                {grievance?.email || 'grievance@misyou.in'}
              </a>
            </p>
            <p>
              <strong>Phone:</strong>{' '}
              <a className="underline" href={`tel:${(grievance?.phone || '+91 9041299739').replace(/\s/g, '')}`}>
                {grievance?.phone || '+91 90412 99739'}
              </a>
            </p>
            <p><strong>Response SLA:</strong> Acknowledgement within 24 hours; resolution within {grievance?.response_sla_days || 7} working days.</p>
            <p className="text-xs mt-2">
              If you remain unsatisfied, you may file a complaint with the
              <strong> Data Protection Board of India</strong> under Chapter V of the DPDP Act, 2023.
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Schedule account deletion</DialogTitle>
            <DialogDescription>
              We will start a 30-day countdown. You can sign in any time during this window
              and click <strong>Cancel deletion</strong> to keep your account. After 30 days
              your account and all associated data will be permanently erased, except where
              we are required by law to retain it (e.g., payment records under the GST &amp;
              Income Tax Acts).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="delete-reason">Why are you leaving? (optional, max 500 chars)</Label>
              <Textarea
                id="delete-reason"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value.slice(0, 500))}
                placeholder="Helps us improve — totally optional."
                rows={3}
                className="mt-1"
              />
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={deleteConfirmed}
                onChange={(e) => setDeleteConfirmed(e.target.checked)}
                data-testid="delete-confirm-checkbox"
              />
              <span>
                I understand my account will be permanently deleted after 30 days unless
                I cancel this request by signing in.
              </span>
            </label>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteOpen(false)} className="flex-1" disabled={submitting}>
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleRequestDelete}
                disabled={!deleteConfirmed || submitting}
                data-testid="confirm-delete-btn"
              >
                {submitting ? 'Scheduling…' : 'Schedule deletion'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdraw consent dialog */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent data-testid="withdraw-consent-dialog">
          <DialogHeader>
            <DialogTitle>Withdraw consent</DialogTitle>
            <DialogDescription>
              You can withdraw your consent at any time. Existing cases and sightings will
              remain visible (they have already been published), but you will not be able
              to submit new ones until you re-accept the policy.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={withdrawConfirmed}
              onChange={(e) => setWithdrawConfirmed(e.target.checked)}
              data-testid="withdraw-confirm-checkbox"
            />
            <span>I understand that I will not be able to post new cases or sightings.</span>
          </label>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setWithdrawOpen(false)} className="flex-1" disabled={submitting}>
              Cancel
            </Button>
            <Button
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleWithdrawConsent}
              disabled={!withdrawConfirmed || submitting}
              data-testid="confirm-withdraw-btn"
            >
              {submitting ? 'Submitting…' : 'Withdraw consent'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nominate dialog */}
      <Dialog open={nominateOpen} onOpenChange={setNominateOpen}>
        <DialogContent data-testid="nominate-dialog">
          <DialogHeader>
            <DialogTitle>Nominate a representative</DialogTitle>
            <DialogDescription>
              In line with DPDP §13, you can nominate one individual who can exercise your
              rights on your behalf if you are unable to. They will be contacted only on
              your written instruction or, in your absence, on receipt of a death/incapacity
              certificate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nominee full name <span className="text-red-600">*</span></Label>
              <Input
                value={nomineeForm.nominee_name}
                onChange={(e) => setNomineeForm({ ...nomineeForm, nominee_name: e.target.value })}
                placeholder="e.g., Sunita Kaur"
                data-testid="nominee-name-input"
              />
            </div>
            <div>
              <Label>Relationship <span className="text-red-600">*</span></Label>
              <Input
                value={nomineeForm.nominee_relationship}
                onChange={(e) => setNomineeForm({ ...nomineeForm, nominee_relationship: e.target.value })}
                placeholder="Spouse / Sibling / Parent / Adult child / Friend / Lawyer"
                data-testid="nominee-relationship-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Email (optional)</Label>
                <Input
                  type="email"
                  value={nomineeForm.nominee_email}
                  onChange={(e) => setNomineeForm({ ...nomineeForm, nominee_email: e.target.value })}
                  placeholder="nominee@example.com"
                  data-testid="nominee-email-input"
                />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input
                  type="tel"
                  value={nomineeForm.nominee_phone}
                  onChange={(e) => setNomineeForm({ ...nomineeForm, nominee_phone: e.target.value })}
                  placeholder="+91 …"
                  data-testid="nominee-phone-input"
                />
              </div>
            </div>
            <div>
              <Label>Note to MISYOU (optional, max 500 chars)</Label>
              <Textarea
                value={nomineeForm.note}
                onChange={(e) => setNomineeForm({ ...nomineeForm, note: e.target.value.slice(0, 500) })}
                rows={2}
                placeholder="Anything we should know — e.g., 'Sunita has my power-of-attorney for medical decisions.'"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setNominateOpen(false)} className="flex-1" disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleSaveNominee} className="flex-1" disabled={submitting} data-testid="save-nominee-btn">
                {submitting ? 'Saving…' : 'Save nominee'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
